/**
 * 整合性チェック (`checkProfileIntegrity`) へ渡す「ModeProfile (M10) 由来の判断材料」を作るフック。
 *
 * 背景 (モードドリブン化 2026-07-24 / issue #45):
 * mode キーは「ラベル/フィルタタグ」であり挙動を決めない。したがって
 * 「M1 の mode と M3/M2 の mode が違う」だけでは誤りとは言えず、**登記簿である ModeProfile が
 * その組合せを推奨しているなら正しい構成**である。mode 文字列の直接比較に代わる判断材料として、
 * 推奨マスタ id 群と mode 表示名リゾルバを供給する。
 *
 * 表示名リゾルバは警告文に内部キー (`ecw-walkable` 等) を出さないためにも使う
 * (「UI に内部識別子を出さない」全体ルール)。
 */

import { getModeLabel, type DistrictMode, type ModeProfile } from '@citizen-walk/shared-types';
import { useMemo } from 'react';
import { useModeProfiles } from './useModeProfiles';

/** ModeProfile.recommendedMasters を、id の平坦な配列に落とす (pure、テスト用に export)。 */
export function recommendedMasterIdsOf(profile: ModeProfile | undefined): string[] {
  const r = profile?.recommendedMasters;
  if (!r) return [];
  return [
    r.analysisSettingsId,
    r.dialogueSettingsId,
    r.displayConfigId,
    r.outputFormatId,
    r.scoreDimensionId,
    ...(r.categoryMasterIds ?? []),
  ].filter((x): x is string => typeof x === 'string' && x.length > 0);
}

/**
 * mode キー → 表示名の解決関数を作る (pure、テスト用に export)。
 * 登記簿に modeLabel があればそれを、無ければ組込モードの固定ラベル、最後に key そのもの。
 */
export function buildModeLabelResolver(
  profiles: readonly ModeProfile[],
): (mode: DistrictMode) => string {
  const byMode = new Map<string, string>();
  for (const p of profiles) {
    const key = p.mode?.trim();
    if (key && p.modeLabel?.trim() && !byMode.has(key)) byMode.set(key, p.modeLabel.trim());
  }
  return (mode: DistrictMode) => getModeLabel(mode, byMode.get(mode));
}

export interface ModeProfileIntegrityContext {
  /** 当該 mode の ModeProfile が推奨するマスタ id 群 (未登録 mode なら空)。 */
  recommendedMasterIds: string[];
  /** mode キー → 表示名。 */
  resolveModeLabel: (mode: DistrictMode) => string;
}

/** 指定 mode の ModeProfile から整合性チェック用の判断材料を取り出す。 */
export function useModeProfileIntegrity(
  mode: DistrictMode | undefined,
): ModeProfileIntegrityContext {
  const state = useModeProfiles({ filterArchived: false });
  const profiles = state.status === 'ready' ? state.items : [];
  return useMemo(() => {
    const profile = mode ? profiles.find((p) => p.mode === mode) : undefined;
    return {
      recommendedMasterIds: recommendedMasterIdsOf(profile),
      resolveModeLabel: buildModeLabelResolver(profiles),
    };
  }, [profiles, mode]);
}
